/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, When } from "react-if";
import { Box, Modal, Text, theme } from "@nordcloud/gnui";
import { LoaderWrap } from "~/components";
import { BoldText } from "~/components/StepContainer/styles";
import { inflect } from "~/tools";
import { useDeleteResource } from "../../hooks";
import { useResource } from "../../ResourceProvider";

type Props = {
  resourcesDetails: {
    id: string;
    name: string;
    scanned: boolean;
  }[];
  isDeleteModalOpen: boolean;
  close: () => void;
  onSuccess?: () => void;
};

type ModalAction = {
  order: number;
  onAction: () => void;
  label: string;
  disabled?: boolean;
  severity: "low" | "medium" | "high";
};

export function DeleteResource({
  resourcesDetails,
  isDeleteModalOpen,
  close,
  onSuccess,
}: Props) {
  const [deleteResource, { loading }] = useDeleteResource({
    onSuccess: () => {
      close();
      onSuccess?.();
    },
  });
  const { cleanRowSelection } = useResource();

  const resourcesIds = resourcesDetails.map((resource) => resource.id);

  async function deleteResources(ids: string[]) {
    return Promise.all(ids.map((id) => deleteResource(id)));
  }

  function onDelete(ids: string[]) {
    deleteResources(ids).finally(() => cleanRowSelection?.());
  }

  const scannedResources = resourcesDetails.filter(
    (resource) => resource.scanned
  );

  const notScannedResources = resourcesDetails.filter(
    (resource) => !resource.scanned
  );

  const notScannedResourcesIds = notScannedResources.map(
    (resource) => resource.id
  );

  const actions: ModalAction[] = [
    {
      order: 0,
      onAction: close,
      label: "Close",
      severity: "low",
    },
    ...(notScannedResourcesIds.length > 0
      ? [
          {
            order: 1,
            onAction: () => onDelete(notScannedResourcesIds),
            label: "Delete",
            disabled: loading,
            severity: "high" as const,
          },
        ]
      : []),
  ];

  return (
    <Modal
      onClose={close}
      isOpen={isDeleteModalOpen}
      actionLabel="Delete"
      contentLabel={`Delete ${inflect("Resource")(resourcesIds.length)}`}
      actions={actions}
    >
      <LoaderWrap inContent loading={loading}>
        <When condition={scannedResources.length > 0}>
          <Text>
            <If condition={scannedResources.length > 1}>
              <Then>
                The following Resources cannot be deleted as they are associated
                with a specific cloud account.
              </Then>
              <Else>
                The following Resource cannot be deleted as it is associated
                with a specific cloud account. To remove a resource, you must
                delete it from the respective account within the cloud
                environment.
              </Else>
            </If>
          </Text>
          <Box mb={theme.spacing.spacing02} boxStyle="lightGrey">
            {scannedResources?.map(({ id, name }) => {
              return <div key={`${id}-${name}`}>{name}</div>;
            })}
          </Box>
        </When>
        <When condition={notScannedResources.length > 0}>
          <Text>
            The {inflect("Resource")(notScannedResources.length)} planned for
            deletion {notScannedResources.length > 1 ? "are" : "is"}:
          </Text>
          <Box mb={theme.spacing.spacing02} boxStyle="lightGrey">
            {notScannedResources?.map(({ id, name }) => {
              return <div key={`${id}-${name}`}>{name}</div>;
            })}
          </Box>
          <Text mt={theme.spacing.spacing04} color={theme.color.text.warning}>
            Please note that this action will effectively{" "}
            <BoldText>
              remove and deregister the{" "}
              {inflect("Resource")(notScannedResources.length)}
            </BoldText>{" "}
            from the system.
          </Text>
          {`Are you sure you want to delete ${inflect("Resource")(
            notScannedResources.length
          )}?`}
        </When>
      </LoaderWrap>
    </Modal>
  );
}
